import React, { useEffect, useState } from 'react';
import { FileText, Flame, FolderOpen, Lightbulb, MessageSquare, PenLine, Trophy, TrendingUp, Zap } from 'lucide-react';
import { Link } from './ui/link';
import { Button } from './ui/button';
import { StatCard } from './StatCard';
import { getIconComponent } from './IconPicker';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { API_BASE } from '../config';
import { toBcp47Locale } from '../locales/locale';
import { getDocumentPanelPath, getDocumentPath } from '../utils/document-panel-paths';

type DeskHomeDocument = {
  id: string;
  projectId: string | null;
  projectTitle: string | null;
  title: string;
  icon: string | null;
};

type DeskHomeMilestone = {
  threshold: number;
  writeDate: string;
  documentId: string;
  documentTitle: string;
  projectId: string | null;
  projectTitle: string | null;
  documentIcon: string | null;
};

type DeskHomeTopWordsDocument = DeskHomeDocument & {
  wordsWritten: number;
};

type DeskHomeUnresolvedDocument = DeskHomeDocument & {
  unresolvedCount: number;
};

type DeskHomeSharedProject = {
  id: string;
  title: string;
  icon: string | null;
  ownerEmail: string | null;
  sharedAt: string;
};

type DeskHomeSharedDocument = DeskHomeDocument & {
  ownerEmail: string | null;
  sharedAt: string;
};

type DeskHomeCommentOrShareDocument = DeskHomeDocument & {
  activityAt: string;
};

type DeskHomeStats = {
  recentDocuments: DeskHomeDocument[];
  wordsWrittenLast7Days: number;
  globalWritingStreakDays: number;
  recentDocumentWritingStreakDays: number;
  mostRecentDocument: DeskHomeDocument | null;
  mostWordsDocumentLast7Days: DeskHomeTopWordsDocument | null;
  recentMilestones: DeskHomeMilestone[];
  documentsWithUnresolvedComments: DeskHomeUnresolvedDocument[];
  documentsWithPendingSuggestions: DeskHomeUnresolvedDocument[];
  recentDocumentCommentsAndShares: DeskHomeCommentOrShareDocument[];
  recentlySharedProjects: DeskHomeSharedProject[];
  recentlySharedDocuments: DeskHomeSharedDocument[];
};

function formatStreakDays(count: number, t: (key: string) => string): string {
  const template =
    count === 1
      ? t('documentStats.longestWritingStreakDay')
      : t('documentStats.longestWritingStreakDays');
  return template.replace('{count}', String(count));
}

function formatMilestoneDate(dateKey: string, locale: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export const DeskHomePanel: React.FC = () => {
  const { token } = useAuth();
  const { t, locale } = useLocale();
  usePageTitle(t('desk'));
  const [stats, setStats] = useState<DeskHomeStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setStats(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadStats = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/desk/home-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch desk home stats');
        }
        const data = (await response.json()) as DeskHomeStats;
        if (!cancelled) {
          setStats({
            ...data,
            recentMilestones: Array.isArray(data.recentMilestones) ? data.recentMilestones : [],
            documentsWithUnresolvedComments: Array.isArray(data.documentsWithUnresolvedComments)
              ? data.documentsWithUnresolvedComments
              : [],
            documentsWithPendingSuggestions: Array.isArray(data.documentsWithPendingSuggestions)
              ? data.documentsWithPendingSuggestions
              : [],
            recentDocumentCommentsAndShares: Array.isArray(data.recentDocumentCommentsAndShares)
              ? data.recentDocumentCommentsAndShares
              : [],
            recentlySharedProjects: Array.isArray(data.recentlySharedProjects)
              ? data.recentlySharedProjects
              : [],
            recentlySharedDocuments: Array.isArray(data.recentlySharedDocuments)
              ? data.recentlySharedDocuments
              : [],
          });
        }
      } catch (error) {
        console.error('Error loading desk home stats:', error);
        if (!cancelled) {
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadStats();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadingValue = loading ? '…' : undefined;
  const recentDocuments = stats?.recentDocuments ?? [];
  const recentMilestones = stats?.recentMilestones ?? [];
  const documentsWithUnresolvedComments = stats?.documentsWithUnresolvedComments ?? [];
  const documentsWithPendingSuggestions = stats?.documentsWithPendingSuggestions ?? [];
  const recentDocumentCommentsAndShares = stats?.recentDocumentCommentsAndShares ?? [];
  const recentlySharedProjects = stats?.recentlySharedProjects ?? [];
  const recentlySharedDocuments = stats?.recentlySharedDocuments ?? [];
  const mostRecentDocument = stats?.mostRecentDocument ?? null;
  const mostWordsDocument = stats?.mostWordsDocumentLast7Days ?? null;
  const recentDocumentStatsHref = mostRecentDocument
    ? getDocumentPanelPath(mostRecentDocument.projectId ?? undefined, mostRecentDocument.id, 'stats')
    : null;
  const mostWordsDocumentStatsHref = mostWordsDocument
    ? getDocumentPanelPath(mostWordsDocument.projectId ?? undefined, mostWordsDocument.id, 'stats')
    : null;

  return (
    <div data-testid="desk-home-panel" className="mb-8 min-w-0 space-y-4">
      <section className="min-w-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10 p-4 sm:p-5">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-foreground">{t('desk_home_recent_documents')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('desk_home_recent_documents_hint')}</p>
        </div>

        <div className="mb-5 flex min-w-0 flex-wrap gap-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : recentDocuments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('desk_home_no_recent_documents')}</p>
          ) : (
            recentDocuments.map((doc) => {
              const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
              return (
                <Button key={doc.id} asChild variant="outline" size="sm" className="min-w-0 max-w-full">
                  <Link
                    to={getDocumentPath(doc.projectId ?? undefined, doc.id)}
                    className="min-w-0 max-w-full overflow-hidden"
                    title={doc.projectTitle ?? undefined}
                  >
                    {DocIcon ? (
                      <DocIcon className="h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                      <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <span className="min-w-0 truncate">{doc.title || t('untitled_document')}</span>
                  </Link>
                </Button>
              );
            })
          )}
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
          <StatCard
            icon={<Flame className="h-4 w-4" aria-hidden />}
            label={t('desk_home_recent_document_largest_streak')}
            value={
              loadingValue
              ?? formatStreakDays(stats?.recentDocumentWritingStreakDays ?? 0, t)
            }
            href={recentDocumentStatsHref ?? undefined}
            subvalue={
              mostRecentDocument ? (
                <Link
                  to={getDocumentPath(
                    mostRecentDocument.projectId ?? undefined,
                    mostRecentDocument.id
                  )}
                  className="mt-0.5 block min-w-0 max-w-full truncate text-muted-foreground transition-colors hover:text-primary hover:underline"
                  title={mostRecentDocument.projectTitle ?? undefined}
                >
                  {mostRecentDocument.title || t('untitled_document')}
                </Link>
              ) : undefined
            }
            className="h-full min-w-0 transition-colors hover:border-primary/40 hover:bg-muted/40"
          />
          <StatCard
            icon={<TrendingUp className="h-4 w-4" aria-hidden />}
            label={t('desk_home_most_words_document_last_7_days')}
            value={
              loadingValue
              ?? (mostWordsDocument
                ? t('desk_home_most_words_document_value').replace(
                  '{count}',
                  mostWordsDocument.wordsWritten.toLocaleString(toBcp47Locale(locale))
                )
                : t('desk_home_most_words_document_value').replace('{count}', '0'))
            }
            href={mostWordsDocumentStatsHref ?? undefined}
            subvalue={
              mostWordsDocument ? (
                <Link
                  to={getDocumentPath(
                    mostWordsDocument.projectId ?? undefined,
                    mostWordsDocument.id
                  )}
                  className="mt-0.5 block min-w-0 max-w-full truncate text-muted-foreground transition-colors hover:text-primary hover:underline"
                  title={mostWordsDocument.projectTitle ?? undefined}
                >
                  {mostWordsDocument.title || t('untitled_document')}
                </Link>
              ) : undefined
            }
            className="h-full min-w-0 transition-colors hover:border-primary/40 hover:bg-muted/40"
          />
        </div>
      </section>

      <section className="min-w-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10 p-4 sm:p-5">
        <h2 className="mb-4 text-base font-semibold text-foreground">{t('desk_home_continue_editing')}</h2>
        <div className="grid min-w-0 grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="min-w-0">
            <h3 className="mb-2 flex items-center gap-3 text-sm font-semibold text-foreground">
              <span className="flex w-8 shrink-0 justify-center text-primary">
                <MessageSquare className="h-4 w-4" aria-hidden />
              </span>
              {t('desk_home_unresolved_comments')}
            </h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : documentsWithUnresolvedComments.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_home_no_unresolved_comments')}</p>
            ) : (
              <ul className="min-w-0 space-y-2 text-sm">
                {documentsWithUnresolvedComments.map((doc) => (
                  <li key={`comments-${doc.id}`} className="min-w-0">
                    <Link
                      to={getDocumentPanelPath(doc.projectId ?? undefined, doc.id, 'comments')}
                      className="flex min-w-0 items-center gap-3 overflow-hidden text-foreground hover:text-primary"
                      title={doc.projectTitle ?? undefined}
                    >
                      <span className="w-8 shrink-0 text-center text-xs font-semibold tabular-nums text-muted-foreground">
                        {doc.unresolvedCount}
                      </span>
                      <span className="min-w-0 truncate">
                        {doc.title || t('untitled_document')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 flex items-center gap-3 text-sm font-semibold text-foreground">
              <span className="flex w-8 shrink-0 justify-center text-primary">
                <Lightbulb className="h-4 w-4" aria-hidden />
              </span>
              {t('desk_home_unresolved_suggestions')}
            </h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : documentsWithPendingSuggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_home_no_unresolved_suggestions')}</p>
            ) : (
              <ul className="min-w-0 space-y-2 text-sm">
                {documentsWithPendingSuggestions.map((doc) => (
                  <li key={`suggestions-${doc.id}`} className="min-w-0">
                    <Link
                      to={getDocumentPanelPath(doc.projectId ?? undefined, doc.id, 'suggestions')}
                      className="flex min-w-0 items-center gap-3 overflow-hidden text-foreground hover:text-primary"
                      title={doc.projectTitle ?? undefined}
                    >
                      <span className="w-8 shrink-0 text-center text-xs font-semibold tabular-nums text-muted-foreground">
                        {doc.unresolvedCount}
                      </span>
                      <span className="min-w-0 truncate">
                        {doc.title || t('untitled_document')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="min-w-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10 p-4 sm:p-5">
        <h2 className="mb-4 text-base font-semibold text-foreground">{t('desk_home_collaboration')}</h2>
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="min-w-0">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
              <MessageSquare className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              {t('desk_home_recent_document_comments_and_shares')}
            </h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : recentDocumentCommentsAndShares.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_home_no_recent_document_comments_and_shares')}</p>
            ) : (
              <ul className="min-w-0 space-y-2 text-sm">
                {recentDocumentCommentsAndShares.map((doc) => {
                  const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
                  return (
                    <li key={`comment-or-share-document-${doc.id}`} className="min-w-0">
                      <Link
                        to={getDocumentPanelPath(doc.projectId ?? undefined, doc.id, 'comments')}
                        className="flex min-w-0 items-center gap-2 overflow-hidden text-foreground hover:text-primary"
                        title={doc.projectTitle ?? undefined}
                      >
                        {DocIcon ? (
                          <DocIcon className="h-4 w-4 shrink-0" aria-hidden />
                        ) : (
                          <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        )}
                        <span className="min-w-0 truncate">
                          {doc.title || t('untitled_document')}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
              <FolderOpen className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              {t('desk_home_recently_shared_projects')}
            </h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : recentlySharedProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_home_no_recently_shared_projects')}</p>
            ) : (
              <ul className="min-w-0 space-y-2 text-sm">
                {recentlySharedProjects.map((project) => {
                  const ProjectIcon = project.icon ? getIconComponent(project.icon) : null;
                  return (
                    <li key={`shared-project-${project.id}`} className="min-w-0">
                      <Link
                        to={`/projects/${project.id}`}
                        className="flex min-w-0 items-center gap-2 overflow-hidden text-foreground hover:text-primary"
                      >
                        {ProjectIcon ? (
                          <ProjectIcon className="h-4 w-4 shrink-0" aria-hidden />
                        ) : (
                          <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        )}
                        <span className="min-w-0 truncate">
                          {project.title || t('untitled_project')}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
              <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              {t('desk_home_recently_shared_documents')}
            </h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : recentlySharedDocuments.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_home_no_recently_shared_documents')}</p>
            ) : (
              <ul className="min-w-0 space-y-2 text-sm">
                {recentlySharedDocuments.map((doc) => {
                  const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
                  return (
                    <li key={`shared-document-${doc.id}`} className="min-w-0">
                      <Link
                        to={getDocumentPath(doc.projectId ?? undefined, doc.id)}
                        className="flex min-w-0 items-center gap-2 overflow-hidden text-foreground hover:text-primary"
                        title={doc.projectTitle ?? undefined}
                      >
                        {DocIcon ? (
                          <DocIcon className="h-4 w-4 shrink-0" aria-hidden />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        )}
                        <span className="min-w-0 truncate">
                          {doc.title || t('untitled_document')}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="min-w-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10 p-4 sm:p-5">
        <h2 className="mb-4 text-base font-semibold text-foreground">{t('desk_home_quick_stats')}</h2>

        <ul className="mb-5 space-y-3 text-sm">
          <li className="flex items-baseline justify-between gap-3 sm:justify-start sm:gap-3">
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <PenLine className="h-4 w-4 shrink-0" aria-hidden />
              {t('desk_home_words_last_7_days')}
            </span>
            <span className="font-semibold tabular-nums text-foreground">
              {loadingValue
                ?? (stats?.wordsWrittenLast7Days ?? 0).toLocaleString(toBcp47Locale(locale))}
            </span>
          </li>
          <li className="flex items-baseline justify-between gap-3 sm:justify-start sm:gap-3">
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <Zap className="h-4 w-4 shrink-0" aria-hidden />
              {t('desk_home_global_writing_streak')}
            </span>
            <span className="font-semibold tabular-nums text-foreground">
              {loadingValue ?? formatStreakDays(stats?.globalWritingStreakDays ?? 0, t)}
            </span>
          </li>
        </ul>

        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Trophy className="h-4 w-4 text-primary" aria-hidden />
            {t('desk_home_recent_milestones')}
          </h3>
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : recentMilestones.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('documentStats.noMilestones')}</p>
          ) : (
            <ul className="min-w-0 space-y-1.5 text-sm">
              {recentMilestones.map((milestone) => (
                <li key={`${milestone.documentId}-${milestone.threshold}`} className="min-w-0">
                  <Link
                    to={getDocumentPanelPath(
                      milestone.projectId ?? undefined,
                      milestone.documentId,
                      'stats'
                    )}
                    className="block min-w-0 truncate text-foreground hover:text-primary"
                    title={milestone.projectTitle ?? undefined}
                  >
                    {t('desk_home_milestone_entry')
                      .replace(
                        '{count}',
                        milestone.threshold.toLocaleString(toBcp47Locale(locale))
                      )
                      .replace(
                        '{document}',
                        milestone.documentTitle || t('untitled_document')
                      )
                      .replace('{date}', formatMilestoneDate(milestone.writeDate, locale))}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};
